import {
  CompRef,
  DocumentServicesObject,
  StudioWidgetComponentData,
  AppDataComponent,
} from '@wix/document-services-types';
import { memoize } from 'lodash';
import { ErrorReporter } from '@wix/editor-error-reporter';
import {
  WidgetPointer,
  WidgetSlot,
  WidgetSlotPosition,
} from '@wix/editor-platform-host-integration-apis';
import { ComponentTypes } from '@wix/editor-const';
import { ComponentType as CompType } from '@wix/ambassador-app-service-webapp/types';
import {
  buildComponentPreviewState,
  createWidgetPluginComponentDefinition,
  getContainer_DEPRECATED_BAD_PERFORMANCE,
  filterPublicSlots,
  getSlotDefinitions,
} from '../utils';
import {
  ComponentPreviewState,
  DEFAULT_BLOCKS_SLOT_KEY_NAME,
} from '../constants';
import { PluginHostApiImplementation } from '../types';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import { SlotPointer } from '../slotPointer';

export interface StudioWidgetComponent {
  type: 'STUDIO_WIDGET';
  name: string;
  componentId: string;
  data: StudioWidgetComponentData['compData']['studioWidgetComponentData'];
}
type AppDataComponentExtended = AppDataComponent | StudioWidgetComponent;

function reportGetWidgetSlotsError(error: any) {
  ErrorReporter.captureException(error, {
    tags: { widgetPlugins: true },
  });
  console.error(error);
}
// NOTE: It's quite heavy, but widget slots are not changed during the session, so we can cache the result
const getWidgetSlotCompRefsCached = memoize(
  function (
    documentServicesAPI: DocumentServicesObject,
    compRef: CompRef,
  ): CompRef[] {
    return getWidgetSlotCompRefs(documentServicesAPI, compRef);
  },
  (documentServicesAPI, compRef) =>
    compRef &&
    `${compRef.id}-${compRef.type}-${documentServicesAPI.components.is.rendered(
      compRef,
    )}`,
);

function getWidgetSlotCompRefsForGfpp(
  documentServicesAPI: DocumentServicesObject,
  compRef: CompRef,
): CompRef[] {
  return getWidgetSlotCompRefsCached(documentServicesAPI, compRef);
}

function isValidStudioVersion(
  component: StudioWidgetComponent | undefined,
): boolean {
  const minValidStudioVersion = 2;

  if (!component) {
    return false;
  }

  const blocksVersion = component.data?.blocksVersion ?? '';
  const [major] = blocksVersion?.split('.');
  return parseInt(major, 10) >= minValidStudioVersion;
}

function isStudioComponent(
  documentServicesAPI: DocumentServicesObject,
  refComponentPointer: CompRef,
): boolean {
  const { appDefinitionId, widgetId, type } =
    documentServicesAPI.components.data.get(refComponentPointer) ?? {};
  if (type !== 'WidgetRef') {
    return false;
  }

  const appData =
    documentServicesAPI.platform.getAppDataByAppDefId(appDefinitionId);
  if (!appData) {
    return false;
  }

  /*
    for some reason runtime type `AppDataComponent` that comes from csm
    missed `STUDIO_WIDGET` component type with expected `blocksVersion` property
    so we manually extend `AppDataComponent` type
  */
  const appDataComponents =
    appData.components as unknown as AppDataComponentExtended[];

  const studioWidgetComponent = appDataComponents.find(
    (component): component is StudioWidgetComponent =>
      component.componentId === widgetId &&
      component.type === CompType.STUDIO_WIDGET,
  );

  return isValidStudioVersion(studioWidgetComponent);
}

function getSlotsFromDm(
  documentServicesAPI: DocumentServicesObject,
  widgetCompRef: CompRef,
): Omit<WidgetSlot, 'placement'>[] {
  if (isStudioComponent(documentServicesAPI, widgetCompRef)) {
    try {
      return documentServicesAPI.components.slots.getWidgetSlots(
        widgetCompRef,
      ) as Omit<WidgetSlot, 'placement'>[]; // incopatibility between inner and DM types, need to fix;
    } catch (e) {
      reportGetWidgetSlotsError(e);
      return [];
    }
  }
  return [];
}

function getWidgetSlotCompRefs(
  documentServicesAPI: DocumentServicesObject,
  widgetCompRef: CompRef,
): CompRef[] {
  return getSlotsFromDm(documentServicesAPI, widgetCompRef).map(
    ({ compRef }) => compRef,
  );
}

function getWidgetHostRef(
  documentServicesAPI: DocumentServicesObject,
  compRef: CompRef,
): CompRef {
  return documentServicesAPI.components.refComponents.isReferredComponent(
    compRef,
  )
    ? documentServicesAPI.components.refComponents.getRootRefHostCompPointer(
        compRef,
      )
    : compRef;
}

function getPlacement(
  documentServicesAPI: DocumentServicesObject,
  slot: Omit<WidgetSlot, 'placement'>,
) {
  const hostRef = getWidgetHostRef(documentServicesAPI, slot.compRef);
  const { widgetId, appDefinitionId } =
    documentServicesAPI.components.data.get(hostRef);
  return {
    appDefinitionId,
    widgetId,
    slotId: slot.role,
  };
}

function isWidgetInWidget(
  documentServicesAPI: DocumentServicesObject,
  compRef: CompRef,
): boolean {
  const widgetCompRef =
    documentServicesAPI.components.getType(compRef) === ComponentTypes.AppWidget
      ? documentServicesAPI.components.refComponents.getRefHostCompPointer(
          compRef,
        )
      : compRef;

  if (!widgetCompRef) {
    return false;
  }

  return (
    documentServicesAPI.components.refComponents.isReferredComponent(
      widgetCompRef,
    ) &&
    documentServicesAPI.components.getType(widgetCompRef) ===
      ComponentTypes.RefComponent
  );
}
export function createBlocksHostAPI(
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
): PluginHostApiImplementation {
  function mergeDmSlotsAndSlotDefinitions(
    dmSlots: ReturnType<typeof getSlotsFromDm>,
    widgetHostRef: CompRef,
    options: { includePrivate: boolean },
  ) {
    const slotDefinitions = getSlotDefinitions(
      documentServicesAPI,
      widgetHostRef,
      experimentsAPI,
    );

    if (slotDefinitions.length > 0) {
      const validSlotsWithIds: typeof dmSlots = [];
      const publicSlots = options.includePrivate
        ? slotDefinitions
        : filterPublicSlots(slotDefinitions);
      publicSlots.forEach((definition) => {
        const validSlotFromDm = dmSlots.find((s) => s.role === definition.name);
        if (validSlotFromDm) {
          validSlotsWithIds.push({
            ...validSlotFromDm,
            id: definition.id,
          });
        }
      });
      return validSlotsWithIds;
    }

    return dmSlots;
  }

  return {
    getWidgetHostRefBySlotContainer(slotPointer: SlotPointer): CompRef {
      const slotRef = slotPointer.toBlocks().slotRef;
      return getWidgetHostRef(documentServicesAPI, slotRef);
    },
    getWidgetHostRef: (compRef: CompRef) =>
      getWidgetHostRef(documentServicesAPI, compRef),
    getSlotPlaceholderDisplayName(compRef: CompRef): string | undefined {
      const stageData =
        documentServicesAPI.platform.controllers.getConnectedComponentStageData(
          compRef,
        );
      return stageData?.displayName;
    },
    getWidgetSlot(
      slotPointer: SlotPointer,
      { includePrivate }: { includePrivate: boolean },
    ): WidgetSlot {
      const slotCompRef = slotPointer.toBlocks().slotRef;
      const hostRef = getWidgetHostRef(documentServicesAPI, slotCompRef);
      const dmSlots = getSlotsFromDm(documentServicesAPI, hostRef);
      const mergedSlots = mergeDmSlotsAndSlotDefinitions(dmSlots, hostRef, {
        includePrivate,
      });
      const slot = mergedSlots.find((s) => s.compRef.id === slotCompRef.id);
      if (!slot) {
        throw new Error(`Slot not found for ${slotCompRef.id}`);
      }
      return {
        ...slot,
        placement: getPlacement(documentServicesAPI, slot),
      };
    },
    getWidgetSlots(
      widgetCompRef: CompRef,
      options: { includePrivate: boolean },
    ): WidgetSlot[] {
      const dmSlots = getSlotsFromDm(documentServicesAPI, widgetCompRef);
      const mergedSlots = mergeDmSlotsAndSlotDefinitions(
        dmSlots,
        widgetCompRef,
        options,
      );
      return mergedSlots.map((slot) => ({
        ...slot,
        placement: getPlacement(documentServicesAPI, slot),
      }));
    },
    async populateWidgetPluginToSlot(
      slotPointer: SlotPointer,
      widgetPointer: WidgetPointer,
    ): Promise<CompRef> {
      const slotComponentRef = slotPointer.toBlocks().slotRef;
      const appData = documentServicesAPI.tpa.app.getDataByAppDefId(
        widgetPointer.appDefinitionId,
      );

      const widgetComponent = await createWidgetPluginComponentDefinition(
        documentServicesAPI,
        widgetPointer,
        appData.applicationId!,
      );

      const compRef: CompRef = documentServicesAPI.components.slots.populate(
        slotComponentRef,
        DEFAULT_BLOCKS_SLOT_KEY_NAME,
        widgetComponent,
      );
      return compRef;
    },
    uninstallWidgetPlugin(slotPointer: SlotPointer) {
      documentServicesAPI.components.slots.remove(
        slotPointer.toBlocks().slotRef,
        DEFAULT_BLOCKS_SLOT_KEY_NAME,
      );
    },
    getWidgetPointer(slotPointer: SlotPointer): WidgetPointer | undefined {
      const contentRef = documentServicesAPI.components.slots.getSlotsData(
        slotPointer.toBlocks().slotRef,
      )[DEFAULT_BLOCKS_SLOT_KEY_NAME];

      if (!contentRef) {
        return undefined;
      }

      const { appDefinitionId, widgetId } =
        documentServicesAPI.components.data.get(contentRef);

      return { appDefinitionId, widgetId };
    },
    getSlotPlaceholderRefByContentRef(
      widgetPluginCompRef: CompRef,
    ): CompRef | undefined {
      return (
        getContainer_DEPRECATED_BAD_PERFORMANCE(
          documentServicesAPI,
          widgetPluginCompRef,
        ) || undefined
      );
    },
    isSlotPublic(slotCompRef: CompRef): boolean {
      const hostRef = getWidgetHostRef(documentServicesAPI, slotCompRef);
      const dmSlots = getSlotsFromDm(documentServicesAPI, hostRef);
      const slotDefinitions = getSlotDefinitions(
        documentServicesAPI,
        hostRef,
        experimentsAPI,
      );

      if (slotDefinitions.length === 0) {
        return true;
      }
      const slotRole = dmSlots.find(
        (s) => s.compRef.id === slotCompRef.id,
      )?.role;
      const slotDefinition = slotDefinitions.find((s) => s.name === slotRole);
      return slotDefinition?.isPublic === true;
    },
    getSlotPosition(compRef: CompRef): WidgetSlotPosition | null {
      const compLayout =
        documentServicesAPI.components.layout.getRelativeToScreenConsideringScroll(
          compRef,
        );
      return {
        y: compLayout.y,
        height: compLayout.height,
      };
    },
    getSlotContentRef(slotPointer: SlotPointer): CompRef | undefined {
      return documentServicesAPI.components.slots.getSlotsData(
        slotPointer.toBlocks().slotRef,
      )[DEFAULT_BLOCKS_SLOT_KEY_NAME];
    },
    getCompRefForSlotSelection(slotPointer: SlotPointer): CompRef {
      return slotPointer.toBlocks().slotRef;
    },
    hasWidgetSlot(widgetCompRef: CompRef): boolean {
      if (isWidgetInWidget(documentServicesAPI, widgetCompRef)) {
        return false;
      }

      const widgetHostRef = getWidgetHostRef(
        documentServicesAPI,
        widgetCompRef,
      );
      const slotDefinitions = getSlotDefinitions(
        documentServicesAPI,
        widgetHostRef,
        experimentsAPI,
      );

      if (slotDefinitions.length > 0) {
        return filterPublicSlots(slotDefinitions).length > 0;
      }

      return (
        getWidgetSlotCompRefsForGfpp(documentServicesAPI, widgetHostRef)
          .length > 0
      );
    },
    async highlightSlot(slotPointer: SlotPointer) {
      const compRef = slotPointer.toBlocks().slotRef;
      documentServicesAPI.documentMode.setComponentPreviewState(
        compRef.id,
        buildComponentPreviewState(
          DEFAULT_BLOCKS_SLOT_KEY_NAME,
          ComponentPreviewState.HIGHLIGHTED,
        ),
      );
      await documentServicesAPI.waitForChangesAppliedAsync();
    },
    unhighlightSlot(slotPointer: SlotPointer) {
      const compRef = slotPointer.toBlocks().slotRef;
      documentServicesAPI.documentMode.setComponentPreviewState(
        compRef.id,
        undefined,
      );
    },
    isPluginComponent(compRef: CompRef): boolean {
      const parentRef = getContainer_DEPRECATED_BAD_PERFORMANCE(
        documentServicesAPI,
        compRef,
      );
      if (!parentRef) {
        return false;
      }

      return (
        documentServicesAPI.components.getType(parentRef) ===
        ComponentTypes.SlotsPlaceholder
      );
    },
    isWidgetSlotPopulated(slotPointer: SlotPointer) {
      return !!documentServicesAPI.components.slots.getSlotsData(
        slotPointer.toBlocks().slotRef,
      )?.[DEFAULT_BLOCKS_SLOT_KEY_NAME];
    },
  };
}
